import { useEffect, useState } from "react";
import type { ProposalAlternate } from "@/lib/opportunity/proposalAlternateTypes";
import { defaultAlternateForArchetype } from "@/lib/opportunity/proposalAlternateTypes";
import { resolveProposalAlternatesForProduct } from "@/lib/opportunity/proposalAlternateClient";
import { proposalPackageSelectOptions } from "@/lib/opportunity/proposalPrimaryPackage";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { OPPORTUNITY_PROPOSAL_ALTERNATES_RENDER_ENABLED } from "@/lib/opportunity/proposalAlternateFlags";
import type { OpportunityDocumentArchetype } from "@/lib/opportunity/documentArchetypes";
import { OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS } from "@/lib/opportunity/documentArchetypes";

type Props = {
  productType: string;
  value: string;
  onChange: (alternateKey: string, alternate: ProposalAlternate) => void;
  disabled?: boolean;
  archetype?: OpportunityDocumentArchetype;
};

export function ProposalAlternatePicker({
  productType,
  value,
  onChange,
  disabled,
  archetype = "proposal",
}: Props) {
  const [choices, setChoices] = useState<ProposalAlternate[]>([]);
  const [loading, setLoading] = useState(false);

  const formatKey = resolveManagerPresetFormatKey(productType);
  const standard = defaultAlternateForArchetype({ formatKey, productType, archetype });
  const familyLabel = OPPORTUNITY_DOCUMENT_ARCHETYPE_LABELS[archetype];

  useEffect(() => {
    if (!OPPORTUNITY_PROPOSAL_ALTERNATES_RENDER_ENABLED) return;
    let cancelled = false;
    setLoading(true);
    void resolveProposalAlternatesForProduct({ formatKey, productType, archetype }).then(
      (alternates) => {
        if (cancelled) return;
        setChoices(
          proposalPackageSelectOptions({
            alternates,
            formatKey,
            productType,
            archetype,
          }),
        );
        setLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [productType, archetype, formatKey]);

  if (!OPPORTUNITY_PROPOSAL_ALTERNATES_RENDER_ENABLED) {
    return (
      <span
        className="text-[10px] text-gray-500 dark:text-zinc-400 max-w-[12rem] text-right"
        title={`Alternate ${familyLabel.toLowerCase()} packages are temporarily disabled. Using the Primary package.`}
      >
        Primary {familyLabel.toLowerCase()}
      </span>
    );
  }

  if (loading || choices.length === 0) return null;

  const options = choices;
  const selectedKey =
    options.find((a) => a.alternate_key === value)?.alternate_key ?? standard.alternate_key;

  return (
    <label className="flex min-w-0 flex-col items-end gap-1 text-right sm:items-center sm:flex-row sm:gap-2">
      <span className="text-xs font-medium text-gray-600 dark:text-zinc-300">
        {familyLabel} package
      </span>
      <select
        value={selectedKey}
        disabled={disabled}
        onChange={(e) => {
          const next = options.find((a) => a.alternate_key === e.target.value);
          if (next) onChange(next.alternate_key, next);
        }}
        className="max-w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-600 dark:bg-zinc-900"
      >
        {options.map((alt) => (
          <option key={`${alt.id}:${alt.alternate_key}`} value={alt.alternate_key}>
            {alt.label}
          </option>
        ))}
      </select>
    </label>
  );
}
